import { ReactElement } from 'react'
import { useParams } from 'react-router-dom'
import { Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { BASE_URL, PROJECT } from 'src/shared/constants/constants'
import { Sidebar, SidebarGroupObject } from 'src/shared/sidebar/Sidebar'
import { useSidebarContext } from 'src/shared/sidebar/SidebarAwareContext'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { gql, useQuery } from 'urql'

const getProjectQueryById = gql`
  query fetchSingleProject($id: Int!) {
    pf_pgv_project_by_pk(id: $id) {
      id
      project_base {
        dossier {
          short_title
          status
          funding_round {
            name
          }
        }
      }
    }
  }
`

export const PfPgvSidebar = (): ReactElement => {
  const { projectId } = useParams()
  const id = parseInt(projectId as string)
  const notificationService = useNotificationService()
  const { getMessage } = useMessageSource()

  const [{ data, error, fetching }, refetchQuery] = useQuery<
    {
      pf_pgv_project_by_pk: Query_Root['pf_pgv_project_by_pk']
    },
    {
      id: number
    }
  >({
    query: getProjectQueryById,
    variables: { id },
  })

  if (error) {
    notificationService.operationFailed()
  }

  useSidebarContext(refetchQuery)

  const dossier = data?.pf_pgv_project_by_pk?.project_base.dossier
  const shortTitle = dossier?.short_title
  const pfPgvProjectId = data?.pf_pgv_project_by_pk?.id
    ? `${getMessage('label.project.id.character.pf_pgv')}${data?.pf_pgv_project_by_pk?.id}`
    : `${getMessage('label.not.available')}`
  const fundingRoundName = dossier?.funding_round?.name
  const status = dossier?.status

  const routeGroups: SidebarGroupObject[] = [
    {
      groupMessageKey: 'label.project',
      list: [
        {
          listMessageKey: 'label.navigation.project.details',
          href: ROUTES.PfPgvDetailsRoot.params({ projectId }),
          routePath: ROUTES.PfPgvDetailsRoot.path,
          roles: ROUTES.PfPgvDetailsRoot.roles,
        },
        {
          listMessageKey: 'label.navigation.project.permissions',
          href: ROUTES.PfPgvPermissions.params({ projectId }),
          routePath: ROUTES.PfPgvPermissions.path,
          roles: ROUTES.PfPgvPermissions.roles,
        },
        {
          listMessageKey: 'label.navigation.project.journal',
          href: ROUTES.PfPgvProjectJournal.params({ projectId }),
          routePath: ROUTES.PfPgvProjectJournal.path,
          roles: ROUTES.PfPgvProjectJournal.roles,
        },
      ],
    },
    {
      groupMessageKey: 'label.application',
      list: [
        {
          listMessageKey: 'label.navigation.application.details',
          href: ROUTES.PfPgvApplicationDetailsRoot.params({ projectId }),
          routePath: ROUTES.PfPgvApplicationDetailsRoot.path,
          roles: ROUTES.PfPgvApplicationDetailsRoot.roles,
        },
        {
          listMessageKey: 'label.navigation.application.committees',
          href: ROUTES.PfPgvApplicationCommittee.params({ projectId }),
          routePath: ROUTES.PfPgvApplicationCommittee.path,
          roles: ROUTES.PfPgvApplicationCommittee.roles,
        },
        {
          listMessageKey: 'label.navigation.application.assessment',
          href: ROUTES.PfPgvApplicationAssessment.params({ projectId }),
          routePath: ROUTES.PfPgvApplicationAssessment.path,
          roles: ROUTES.PfPgvApplicationAssessment.roles,
        },
      ],
    },
    {
      groupMessageKey: 'label.implementation',
      list: [
        {
          listMessageKey: 'label.navigation.project.implementation',
          href: ROUTES.PfPgvImplementationDetailsRoot.params({ projectId }),
          routePath: ROUTES.PfPgvImplementationDetailsRoot.path,
          roles: ROUTES.PfPgvImplementationDetailsRoot.roles,
        },
      ],
    },
  ]

  return (
    <Sidebar
      title={shortTitle}
      entityId={pfPgvProjectId}
      fundingRoundName={fundingRoundName}
      status={status}
      routes={routeGroups}
      process={PROJECT.PF_PGV}
      home={{ url: BASE_URL.PF_PGV, label: getMessage('label.navigation.pfpgv.cockpit') }}
      loading={fetching}
    />
  )
}
